import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  Home,
  Dumbbell,
  Activity,
  User,
  LogOut,
  BarChart3,
  Map,
  PieChart, // Imported for SWOT
  CalendarDays
} from "lucide-react";

export default function AthleteSidebar({ selectedProgram, userId }) {
  const [collapsed, setCollapsed] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const location = useLocation();
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    let isMounted = true;

    async function fetchScheduleEligibility() {
      if (!userId) {
        if (isMounted) setShowSchedule(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_program_follows")
        .select("programs(program_type)")
        .eq("user_id", userId);

      if (error) {
        console.error("Error checking skills trainer follows:", error);
        if (isMounted) setShowSchedule(false);
        return;
      }

      const hasSkillsTrainerFollow = (data || []).some((row) => {
        const programType = row?.programs?.program_type;
        return typeof programType === "string"
          && programType.toLowerCase().includes("skills trainer");
      });

      if (isMounted) setShowSchedule(hasSkillsTrainerFollow);
    }

    fetchScheduleEligibility();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  // --- 1. DESKTOP SIDEBAR ---
  const DesktopSidebar = () => {
    const navItem = (to, Icon, label) => (
      <Link
        to={to}
        className={`flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-800 transition ${
          isActive(to) ? "bg-gray-800 text-white" : "text-gray-300"
        }`}
        title={collapsed ? label : ""}
      >
        <Icon size={18} />
        {!collapsed && label}
      </Link>
    );

    return (
      <aside
        className={`hidden md:flex flex-col bg-gray-900 text-gray-100 h-screen overflow-hidden transition-all duration-300 border-r border-gray-800 ${
          collapsed ? "w-16" : "w-64"
        }`}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
      >
        <div className="flex flex-col h-screen overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-800 flex flex-col items-center">
            <img
              src={brandLogo}
              alt="Nuvio"
              className="w-8 h-8 rounded-lg object-cover shadow-sm"
            />
            <h1
              className={`text-xl font-bold transition-all duration-300 whitespace-nowrap ${
                collapsed ? "opacity-0 w-0" : "opacity-100"
              }`}
            >
              Nuvio Sport
            </h1>

            {!collapsed && selectedProgram && (
              <div className="mt-3 w-full p-2 text-center bg-gray-700 rounded shadow-sm">
                <span className="font-semibold text-sm">{selectedProgram.name}</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-2 overflow-y-auto">
            {navItem("/", Home, "Dashboard")}
            {navItem("/skills-workouts", Activity, "Skills Workouts")}
            {navItem("/sc-workouts", Dumbbell, "S&C Workouts")}
            
            {/* Grouping Analysis Tools Together */}
            <div className="pt-2 pb-1">
              <div className={`text-[10px] font-bold text-gray-500 uppercase tracking-wider px-3 mb-1 ${collapsed ? "hidden" : "block"}`}>
                Performance Data
              </div>
              {navItem("/combine-scores", BarChart3, "Combine Scores")}
              {navItem("/swot", PieChart, "SWOT Analysis")}
            </div>

            {showSchedule && navItem("/schedule", CalendarDays, "Schedule")}
            {navItem("/marketplace", Map, "Marketplace")}
            {navItem("/profile", User, "Profile")}

            <div className="mt-auto pt-4">
              <Link
                to="/logout"
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-800 transition text-gray-300"
              >
                <LogOut size={18} />
                {!collapsed && "Logout"}
              </Link>
            </div>
          </nav>
        </div>
      </aside>
    );
  };

  // --- 2. MOBILE TOP BAR (Updated) ---
  const MobileTopBar = () => (
    <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-4 flex justify-between items-center shadow-md pt-[env(safe-area-inset-top)] h-[calc(4rem+env(safe-area-inset-top))]">
       <div className="flex flex-col py-2">
          <span className="text-[10px] text-blue-500 font-black uppercase tracking-wider">Athlete View</span>
          <span className="text-white font-bold text-sm truncate max-w-[160px]">
            {selectedProgram?.name || "Nuvio Sport"}
          </span>
       </div>
       
       {/* Right Side Icons Container */}
       <div className="flex items-center gap-2">
         {/* SWOT Icon added here */}
         <Link to="/swot" className={`p-2 rounded-full transition-colors ${isActive('/swot') ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"}`}>
            <PieChart size={18} />
         </Link>

         {showSchedule && (
           <Link to="/schedule" className={`p-2 rounded-full transition-colors ${isActive('/schedule') ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"}`}>
              <CalendarDays size={18} />
           </Link>
         )}

         <Link to="/marketplace" className={`p-2 rounded-full transition-colors ${isActive('/marketplace') ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"}`}>
            <Map size={18} />
         </Link>
       </div>
    </div>
  );

  // --- 3. MOBILE BOTTOM NAV ---
  const MobileBottomNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-xl border-t border-gray-800 pt-2 pb-0">
      <div className="grid grid-cols-5 h-16 items-center px-1">
        <MobileNavItem to="/skills-workouts" icon={Activity} label="Skills" />
        <MobileNavItem to="/sc-workouts" icon={Dumbbell} label="S&C" />

        <div className="relative -top-5 flex justify-center">
          <Link
            to="/"
            className={`flex items-center justify-center w-14 h-14 rounded-full shadow-lg border-4 border-gray-900 transition-all active:scale-95 ${
              isActive("/")
                ? "bg-blue-600 text-white shadow-blue-900/40"
                : "bg-gray-800 text-gray-400"
            }`}
          >
            <Home size={24} />
          </Link>
        </div>

        <MobileNavItem to="/combine-scores" icon={BarChart3} label="Combine" />
        <MobileNavItem to="/profile" icon={User} label="Profile" />
      </div>
    </div>
  );

  const MobileNavItem = ({ to, icon: Icon, label }) => {
    const active = isActive(to);
    return (
      <Link 
        to={to} 
        className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
          active ? "text-blue-500" : "text-gray-500 hover:text-gray-300"
        }`}
      >
        <Icon size={22} strokeWidth={active ? 2.5 : 2} />
        <span className="text-[9px] font-medium">{label}</span>
      </Link>
    );
  };

  return (
    <>
      <MobileTopBar />
      <DesktopSidebar />
      <MobileBottomNav />
    </>
  );
}
